import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { toast } from "sonner";
import { AuthLayout } from "@/components/AuthLayout";
import { GoogleButton, AuthDivider } from "@/components/GoogleButton";
import { GOOGLE_ERROR_MESSAGES } from "@/lib/authErrors";
import { SEO } from "@/components/SEO";

export default function Login() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const redirect = params.get("redirect") || params.get("next") || "";
  const oauthError = params.get("error");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState({ email: "", password: "", form: "" });

  // Surface OAuth failure messages once when redirected back from Google.
  useEffect(() => {
    if (!oauthError) return;
    const msg = GOOGLE_ERROR_MESSAGES[oauthError] || "Google sign-in failed. Please try again.";
    toast.error(msg);
    // Clean the URL so the message doesn't linger on refresh.
    params.delete("error");
    const clean = params.toString();
    navigate(clean ? `/login?${clean}` : "/login", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oauthError]);

  // If already authenticated (e.g. after Google OAuth cookie), jump to destination.
  useEffect(() => {
    if (user && user.email) {
      navigate(redirect || (user.role === "admin" ? "/admin" : "/dashboard"), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldError({ email: "", password: "", form: "" });
    setSubmitting(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back!");
      navigate(redirect || (u.role === "admin" ? "/admin" : "/dashboard"));
    } catch (err) {
      const status = err.response?.status;
      const detail = formatApiErrorDetail(err.response?.data?.detail) || "Login failed";
      if (status === 404) setFieldError({ email: detail, password: "", form: "" });
      else if (status === 401) setFieldError({ email: "", password: detail, form: "" });
      else setFieldError({ email: "", password: "", form: detail });
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthLayout testId="login-page">
      <SEO title="Log In | SchengenFile" description="Sign in to your SchengenFile dashboard to track orders and download your visa documents." noindex />
      <h1 className="font-display font-extrabold text-3xl text-[#0f1b2d]">Welcome back</h1>
      <p className="mt-2 text-sm text-[#475569]">
        Sign in to access your dashboard and download your documents.
      </p>

      <div className="mt-8">
        <GoogleButton nextPath={redirect} testId="login-google-btn" />
        <AuthDivider>or continue with email</AuthDivider>

        <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form" noValidate>
          <Field
            label="Email"
            type="email"
            testid="login-email"
            value={email}
            onChange={setEmail}
            error={fieldError.email}
            autoComplete="email"
          />

          <div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold text-[#0f1b2d]">Password</label>
              <Link
                to="/forgot-password"
                data-testid="link-forgot-password"
                className="text-xs font-semibold text-[#2563eb] hover:text-[#1d4ed8]"
              >
                Forgot password?
              </Link>
            </div>
            <div className="relative mt-1.5">
              <input
                type={showPw ? "text" : "password"}
                required
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                className={`w-full h-11 border rounded-md pl-3 pr-11 text-sm outline-none transition-colors focus:ring-2 focus:ring-[#2563eb]/20 ${
                  fieldError.password
                    ? "border-[#dc2626] focus:border-[#dc2626]"
                    : "border-[#e2e8f0] focus:border-[#2563eb]"
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPw((s) => !s)}
                data-testid="login-password-toggle"
                aria-label={showPw ? "Hide password" : "Show password"}
                className="absolute inset-y-0 right-0 w-11 grid place-items-center text-[#64748b] hover:text-[#0f1b2d]"
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {fieldError.password && (
              <p data-testid="login-password-error" className="mt-1.5 text-xs text-[#dc2626]">
                {fieldError.password}
              </p>
            )}
          </div>

          {fieldError.form && (
            <p data-testid="login-form-error" className="text-sm text-[#dc2626]">
              {fieldError.form}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            data-testid="login-submit-btn"
            className="w-full h-11 flex items-center justify-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-md transition-colors"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? "Signing in…" : "Log in"}
          </button>
        </form>
      </div>

      <div className="mt-8 text-sm text-[#475569] text-center">
        New to SchengenFile?{" "}
        <Link to="/register" data-testid="link-register" className="text-[#2563eb] font-semibold hover:text-[#1d4ed8]">
          Create an account
        </Link>
      </div>
    </AuthLayout>
  );
}

function Field({ label, type = "text", testid, value, onChange, error, autoComplete }) {
  return (
    <div>
      <label className="text-sm font-semibold text-[#0f1b2d]">{label}</label>
      <input
        required
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        className={`mt-1.5 w-full h-11 border rounded-md px-3 text-sm outline-none transition-colors focus:ring-2 focus:ring-[#2563eb]/20 ${
          error ? "border-[#dc2626] focus:border-[#dc2626]" : "border-[#e2e8f0] focus:border-[#2563eb]"
        }`}
      />
      {error && (
        <p data-testid={`${testid}-error`} className="mt-1.5 text-xs text-[#dc2626]">
          {error}
        </p>
      )}
    </div>
  );
}
